import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { LegalPage, H2, P, UL } from '../components/LegalPage';

const LeitpalelTerms = () => {
  const { lang } = useLanguage();

  if (lang === 'en') {
    return (
      <LegalPage
        testid="leitpalel-terms-en"
        eyebrow="Leitpalel for Dummies"
        title="Terms of Service"
        subtitle="Operated by Inversiones Tair SpA"
        lastUpdated="Last updated: February 2026"
      >
        <P>
          Welcome to <strong className="text-white">Leitpalel for Dummies</strong> (the "App"), a service
          of <strong className="text-white">Inversiones Tair SpA</strong> (tair.site). By installing or
          using the App, you accept these terms. If you do not agree, please do not use the App.
        </P>

        <H2>1. Nature of the service</H2>
        <P>
          Leitpalel for Dummies is an educational and devotional tool to learn and accompany Jewish
          prayers (Shajarit, Minjá, Arvit), Brajot, the weekly Parashá and other religious texts. It
          offers word-by-word karaoke audio, transliteration, Hebrew reading and translation in several
          languages.
        </P>
        <P>
          The App is a private study and devotion guide. It does not replace the advice of a Rabbi, the
          attendance at a synagogue or formal halachic rulings. If you have religious doubts, please
          consult your Rabbi.
        </P>

        <H2>2. User account</H2>
        <UL items={[
          'You must be 13+ to use the App.',
          'You are responsible for keeping your password confidential.',
          'You must not share your account with third parties.',
          'We may suspend accounts that abuse the service or violate these terms.',
        ]} />

        <H2>3. Subscriptions and payments</H2>
        <P strong><strong className="text-white">Freemium model:</strong></P>
        <UL items={[
          'Permanent free content: 2 essential Shajarit prayers and 3 main Brajot.',
          'Free trial: 3 days of full Premium access on signup.',
          'Premium subscription: access to all prayers, all festivals, Synagogue Mode, full Daily Study and Torah.',
        ]} />
        <UL items={[
          <>Payments are processed exclusively via the <strong className="text-white">Apple App Store</strong>. Apple Media Services terms apply.</>,
          'The subscription auto-renews at the end of each period unless cancelled at least 24 hours before expiration.',
          'You can cancel anytime from Settings > Apple ID > Subscriptions on your iPhone/iPad.',
          'Refunds are handled directly by Apple, not by Inversiones Tair SpA.',
        ]} />

        <H2>4. Acceptable use</H2>
        <UL items={[
          'Use the App only for personal, educational and devotional purposes.',
          'Do not copy, redistribute or resell the audios and texts.',
          'Do not reverse-engineer or attempt to access the backend.',
          'Do not use the App to offend, manipulate or impersonate others.',
        ]} />

        <H2>5. Intellectual property</H2>
        <P>
          Code, design, audio narration, transliterations and translations are property of Inversiones
          Tair SpA, except for traditional liturgical texts which belong to the Jewish public domain.
          Sefaria and Hebcal are trademarks of their respective owners.
        </P>
        <P>
          We grant you a personal, non-exclusive and non-transferable licence to use the App on the
          devices associated with your Apple ID.
        </P>

        <H2>6. Availability</H2>
        <P>
          We strive to keep the App available 24/7, but we may suspend the service for maintenance,
          updates or force majeure without prior notice. We do not guarantee uninterrupted availability.
        </P>

        <H2>7. Limitation of liability</H2>
        <P>
          The App is provided "as is". To the maximum extent permitted by law, Inversiones Tair SpA shall
          not be liable for:
        </P>
        <UL items={[
          'Typographical inaccuracies or errata in religious texts.',
          'Differences between nusajim, local customs or particular halachic rulings of your community.',
          'Indirect damages arising from the use or impossibility of use of the App.',
        ]} />

        <H2>8. Modifications</H2>
        <P>
          We may update these terms. New versions will be notified within the App. Continued use after
          a change implies acceptance.
        </P>

        <H2>9. Governing law</H2>
        <P>
          These terms are governed by the laws of the country where Inversiones Tair SpA has its
          operating headquarters (Chile), without prejudice to the consumer rights of your country of
          residence.
        </P>

        <H2>10. Contact</H2>
        <P>
          For any inquiry about these terms:{' '}
          <a href="mailto:info@tair.site" className="text-amber-300 hover:text-amber-200">info@tair.site</a>
        </P>
      </LegalPage>
    );
  }

  // Spanish (default)
  return (
    <LegalPage
      testid="leitpalel-terms-es"
      eyebrow="Leitpalel for Dummies"
      title="Términos y Condiciones de Uso"
      subtitle="Operada por Inversiones Tair SpA"
      lastUpdated="Última actualización: Febrero 2026"
    >
      <P>
        Bienvenido a <strong className="text-white">Leitpalel for Dummies</strong> (la "App"), un
        servicio de <strong className="text-white">Inversiones Tair SpA</strong> (tair.site). Al instalar
        o usar la App, aceptas estos términos. Si no estás de acuerdo, te pedimos no usar la App.
      </P>

      <H2>1. Naturaleza del servicio</H2>
      <P>
        Leitpalel for Dummies es una herramienta educativa y devocional para aprender y acompañar los
        rezos judíos (Shajarit, Minjá, Arvit), las Brajot, la Parashá de la semana y otros textos
        religiosos. Ofrece audio karaoke palabra por palabra, transliteración, lectura en hebreo y
        traducción en varios idiomas.
      </P>
      <P>
        La App es una guía de estudio y devoción privada. No reemplaza el consejo de un Rabino ni la
        asistencia a una sinagoga ni decisiones halájicas formales. Si tienes dudas religiosas, consulta
        a tu Rabino.
      </P>

      <H2>2. Cuenta de usuario</H2>
      <UL items={[
        'Debes ser mayor de 13 años para usar la App.',
        'Eres responsable de mantener la confidencialidad de tu contraseña.',
        'No debes compartir tu cuenta con terceros.',
        'Podemos suspender cuentas que abusen del servicio o violen estos términos.',
      ]} />

      <H2>3. Suscripciones y pagos</H2>
      <P strong><strong className="text-white">Modelo freemium:</strong></P>
      <UL items={[
        'Contenido gratuito permanente: 2 rezos esenciales de Shajarit y 3 Brajot principales.',
        'Prueba gratuita: 3 días de acceso completo Premium al crear tu cuenta.',
        'Suscripción Premium: acceso a todos los rezos, todas las festividades, Modo Sinagoga, Estudio Diario completo y Torá.',
      ]} />
      <UL items={[
        <>Los pagos se procesan exclusivamente a través de <strong className="text-white">Apple App Store</strong>. Aplican los términos de Apple Media Services.</>,
        'La suscripción se renueva automáticamente al final de cada periodo salvo que la canceles al menos 24 horas antes del vencimiento.',
        'Puedes cancelar en cualquier momento desde Ajustes > Apple ID > Suscripciones de tu iPhone/iPad.',
        'Los reembolsos los gestiona Apple directamente, no Inversiones Tair SpA.',
      ]} />

      <H2>4. Uso aceptable</H2>
      <P>Te comprometes a:</P>
      <UL items={[
        'Usar la App únicamente con fines personales, educativos y devocionales.',
        'No copiar, redistribuir ni revender los audios y textos.',
        'No realizar ingeniería inversa ni intentar acceder al backend.',
        'No usar la App para ofender, manipular o suplantar a otros.',
      ]} />

      <H2>5. Propiedad intelectual</H2>
      <P>
        El código, diseño, audios narrados, transliteraciones y traducciones son propiedad de
        Inversiones Tair SpA, salvo los textos litúrgicos tradicionales que pertenecen al dominio
        público judío. Sefaria y Hebcal son marcas de sus respectivos titulares.
      </P>
      <P>
        Te otorgamos una licencia personal, no exclusiva e intransferible para usar la App en los
        dispositivos asociados a tu Apple ID.
      </P>

      <H2>6. Disponibilidad</H2>
      <P>
        Hacemos lo posible para mantener la App disponible 24/7, pero podemos suspender el servicio por
        mantenimiento, actualizaciones o causas de fuerza mayor sin previo aviso. No garantizamos
        disponibilidad ininterrumpida.
      </P>

      <H2>7. Limitación de responsabilidad</H2>
      <P>
        La App se ofrece "tal cual". En la medida máxima permitida por la ley, Inversiones Tair SpA no
        será responsable de:
      </P>
      <UL items={[
        'Imprecisiones tipográficas o erratas en textos religiosos.',
        'Diferencias entre nusajim, costumbres locales o decisiones halájicas particulares de tu comunidad.',
        'Daños indirectos derivados del uso o imposibilidad de uso de la App.',
      ]} />

      <H2>8. Modificaciones</H2>
      <P>
        Podemos actualizar estos términos. Las versiones nuevas serán notificadas en la App. El uso
        continuado tras una modificación implica aceptación.
      </P>

      <H2>9. Ley aplicable</H2>
      <P>
        Estos términos se rigen por las leyes del país donde Inversiones Tair SpA tiene su sede
        operativa (Chile), sin perjuicio de los derechos del consumidor de tu país de residencia.
      </P>

      <H2>10. Contacto</H2>
      <P>
        Para cualquier consulta sobre estos términos:{' '}
        <a href="mailto:info@tair.site" className="text-amber-300 hover:text-amber-200">info@tair.site</a>
      </P>
    </LegalPage>
  );
};

export default LeitpalelTerms;
